import { Box, Center, Image, rem } from '@mantine/core'

import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'

import { BOGGLE_RATING_BADGE_IMAGES } from './ratingAssets'
import classes from './RatingBadge.module.css'

const RATING_LABELS = Object.fromEntries(
  BOGGLE_RUN_SUMMARY_RATINGS.map(rating => [rating.id, rating.label]),
) as Record<BoggleRunSummaryRatingId, string>

const DEFAULT_RATING_BADGE_SIZE_PX = 24

function parseRatingBadgeDimension(value: number | string | undefined): number | null {
  if (value === undefined) {
    return null
  }
  if (typeof value === 'number') {
    return value
  }
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : null
}

function resolveRatingBadgeSizePx(h?: number | string, w?: number | string): number {
  const heightPx = parseRatingBadgeDimension(h)
  const widthPx = parseRatingBadgeDimension(w)
  if (heightPx !== null && widthPx !== null) {
    return Math.min(heightPx, widthPx)
  }
  if (heightPx !== null) {
    return heightPx
  }
  if (widthPx !== null) {
    return widthPx
  }
  return DEFAULT_RATING_BADGE_SIZE_PX
}

/**
 * Tier medal for a run or word rating. Unrated (`none`) is a transparent circle
 * with a scaled inset ring (10% of badge size, `#ffffff4a`).
 */
export function RatingBadge({
  className,
  h,
  ratingId,
  testId,
  w,
}: {
  className?: string
  h?: number | string
  ratingId: BoggleRunSummaryRatingId
  testId?: string
  w?: number | string
}) {
  const useDefaultSize = h === undefined && w === undefined && className === undefined

  if (ratingId === 'none') {
    const sizePx = resolveRatingBadgeSizePx(h, w)
    const ringWidthPx = sizePx * 0.05
    return (
      <Center w={w ?? sizePx} h={h ?? sizePx}>
        <Box
          aria-label="Unrated"
          className={[classes.badge, classes.unratedCircle, className]
            .filter(Boolean)
            .join(' ')}
          data-testid={testId}
          role="img"
          w="60%"
          h="auto"
          style={{
            boxShadow: `inset 0 0 0 ${rem(ringWidthPx)} #ffffff4a`,
            flexShrink: 0,
            aspectRatio: '1/1',
          }}
        />
      </Center>
    )
  }

  const src = BOGGLE_RATING_BADGE_IMAGES[ratingId]
  if (!src) {
    return null
  }

  const alt = RATING_LABELS[ratingId] || ratingId
  const badgeClassName = [
    classes.badge,
    useDefaultSize ? classes.sizeMd : null,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Image
      alt={alt}
      className={badgeClassName}
      data-testid={testId}
      fit="contain"
      h={h}
      src={src}
      w={w}
    />
  )
}
